import { FC, lazy, Suspense } from "react";
import { match } from 'react-router-dom';
import { Spinner, Bullseye } from "@patternfly/react-core";
import { RHCErrorPage } from '@rhc-shared-components/error-page';

export interface ComponentLoaderProps {
    componentName: string;
    match?: match;
}

// Lazy loaded component list
export enum EComponentList {
  ImageList = "ImageList",
  FourOhFour = "FourOhFour",
  SetupPreflight = "SetupPreflight",
  TestingOptions = "TestingOptions",
  UpdateGraph = "UpdateGraph",
  SellOnMarketplace = "SellOnMarketplace",
  Vulnerabilities = "Vulnerabilities",
  Certifications = "Certifications",
  Security = "Security",
  SharedTestResults = "SharedTestResults",
  RepositoryInfo = "RepositoryInfo",
  ComponentDetails = "ComponentDetails",
  ContactsInfo = "ContactsInfo",
  OptionalQualifications = "OptionalQualifications",
  AssociatedProduct = "AssociatedProduct"
}

const FourNotFour = () => {
  return <RHCErrorPage errorStatus={404}/>
};

const getComponentPath = (componentName: string): Promise<any> => {
  switch (componentName) {
    case EComponentList.RepositoryInfo:
      return import(/* webpackChunkName: "RepositoryInfo" */ "../../RepositoryInfo/RepositoryInfo");
    case EComponentList.ImageList:
      return import(/* webpackChunkName: "ImageList" */ "../../Images/ImageListWrapper");
    case EComponentList.SetupPreflight:
      return import(/* webpackChunkName: "SetupPreflight" */"../../Images/Preflight/SetupPreflight");
    case EComponentList.TestingOptions:
      return import(/* webpackChunkName: "TestingOptions" */ '../../Certification/Operators/TestingOptions/TestingOptions');
    case EComponentList.UpdateGraph:
      return import(/* webpackChunkName: "UpdateGraph" */ "../../UpdateGraph/UpdateGraph");
    case EComponentList.SellOnMarketplace:
      return import(/* webpackChunkName: "SellOnMarketplace" */ "../../Certification/Operators/SellOnMarketplace/SellOnMarketplace");
    case EComponentList.Vulnerabilities:
      return import(/* webpackChunkName: "Vulnerabilities" */ "../../Security/Vulnerabilities/Vulnerabilities");
    case EComponentList.Certifications:
      return import(/* webpackChunkName: "CertificationsTab" */ "../../Certification/CertificationWrapper");
    case EComponentList.SharedTestResults:
      return import(/* webpackChunkName: "SharedTestResults" */ "../../Certification/SharedTestResults/SharedTestResults");
    case EComponentList.Security:
      return import(/* webpackChunkName: "Security" */ "../../Security/SecurityWrapper");
    case EComponentList.ComponentDetails:
      return import(/* webpackChunkName: "ComponentDetails" */ "../../ComponentDetails/ComponentDetails");
    case EComponentList.ContactsInfo:
      return import(/* webpackChunkName: "ContactInfo" */ "../../ContactInfo/ContactInfo");
    case EComponentList.OptionalQualifications:
      return import(/* webpackChunkName: "OptionalQualifications" */ "../../OptionalQualifications/OptionalQualifications");
    case EComponentList.AssociatedProduct:
      return import(/* webpackChunkName: "AssociatedProduct" */ "../../AssociatedProducts/AssociatedProducts");
    case EComponentList.FourOhFour:
    default:
      return new Promise(resolve => resolve(FourNotFour));
  }
};

const ComponentLoader: FC<ComponentLoaderProps> = ({ componentName, match }) => {
  const Component = lazy(() => getComponentPath(componentName));

  return (
    <Suspense fallback={<Bullseye><Spinner /></Bullseye>}>
      <Component match={match}/>
    </Suspense>
  );
};

export default ComponentLoader;
